import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  DgaIcon,
  DgaButton,
  DgaRectangleSkeleton,
  DgaSquareSkeleton,
  DgaLineSkeleton,
} from "platformscode-new-react";
import NewsCard from "../../../components/shared/NewsCard";
import { getHomeNews } from '../../../services/api';
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
const newsIcon = "assets2/imges/NewsPattern.svg";

const PrevArrow = (props) => (
  <button {...props} className="custom-arrow left-arrow">
    <DgaIcon color="#fff" icon="arrow-left-01" size={24} type="" variant="stroke" />
  </button>
);

const NextArrow = (props) => (
  <button {...props} className="custom-arrow right-arrow">
    <DgaIcon color="#fff" icon="arrow-right-01" size={24} type="" variant="stroke" />
  </button>
);

function NewsSection() {
  const sectionRef = useRef(null);
  const [news, setNews]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  // Defer API fetch until the section is near the viewport.
  // Prevents news card images from downloading during the LCP window.
  useEffect(() => {
    let cancelled = false;

    const doFetch = async () => {
      try {
        const data = await getHomeNews();
        if (!cancelled) setNews(data.data || []);
      } catch (err) {
        if (!cancelled) {
          console.error("خطأ في تحميل الأخبار:", err);
          setError(err.message);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    const el = sectionRef.current;
    if (!el || !('IntersectionObserver' in window)) {
      doFetch();
      return () => { cancelled = true; };
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        observer.disconnect();
        doFetch();
      },
      { rootMargin: '600px' }
    );
    observer.observe(el);
    return () => { cancelled = true; observer.disconnect(); };
  }, []);

  const total        = news.length;
  const slidesToShow = useMemo(() => Math.min(total || 1, 3), [total]);

  const sliderSettings = useMemo(
    () => ({
      rtl: true,
      dots: false,
      arrows: false,
      nextArrow: <PrevArrow />,
      prevArrow: <NextArrow />,
      speed: 400,
      slidesToShow,
      slidesToScroll: 1,
      initialSlide: 0,
      swipeToSlide: true,
      centerMode: false,
      adaptiveHeight: false,
      responsive: [
        { breakpoint: 1260, settings: { slidesToShow: Math.min(total || 1, 2), arrows: true } },
        { breakpoint: 765,  settings: { slidesToShow: 1, arrows: true } },
        { breakpoint: 640,  settings: { slidesToShow: 1, arrows: true } },
      ],
    }),
    [total, slidesToShow]
  );

  return (
    <div id="news-section" ref={sectionRef}>
      {loading ? (
        <div className="cw-full">
          <div className="m-4 flex justify-between">
            <h2 data-aos="fade-in" className="display-sm-bold text-[#161616] mb-4" aria-label="الأخبار">
              <DgaRectangleSkeleton size="md" type="1" width="long" />
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-1 lg:grid-cols-3 gap-6">
            {Array.from({ length: 3 }).map((_, index) => (
              <div
                key={index}
                className="grid gap-[24px] rounded-lg"
                style={{ border: "1.5px solid #0c111b17", padding: "16px" }}
              >
                <DgaSquareSkeleton
                  type="1"
                  height="240px"
                  width="240px"
                  style={{ width: "372.667px", height: "240px", borderRadius: "10px" }}
                />
                <div className="grid gap-[8px]">
                  <DgaLineSkeleton type="1" size="sm" className="mb-1" />
                  <DgaRectangleSkeleton size="md" type="1" width="long" className="mb-2" />
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : error ? (
        <div className="flex justify-center items-center h-64">
          <p className="text-xl text-red-500">
            حدث خطأ أثناء تحميل الأخبار. يرجى المحاولة مرة أخرى لاحقًا.
          </p>
        </div>
      ) : !total ? (
        <div className="flex justify-center items-center h-64">
          <p className="text-xl">لا توجد أخبار متاحة حالياً</p>
        </div>
      ) : (
        <div className="cw-full">
          <div className="m-4 flex justify-between mb-4">
            <div data-aos="fade-in" className="flex gap-[20px]">
              <h2 className="display-sm-bold text-[#161616]">الأخبار</h2>
            </div>
            <DgaButton
              label="عرض الكل"
              variant="secondary-outline"
              size="lg"
              onClick={() => (window.location.href = "/news")}
            />
          </div>
          <div data-aos="fade-in" className="relative">
            <Slider {...sliderSettings}>
              {news.map((item) => (
                <div key={item.id} className="px-2 rtl">
                  <NewsCard data={item} />
                </div>
              ))}
            </Slider>
          </div>
        </div>
      )}
      <style>{`
        .slick-slide { outline: none; }
        .slick-dots { bottom: -30px; }
        .slick-arrow { width: 36px; height: 36px; }
        .slick-prev:before, .slick-next:before { font-size: 28px; }
        .rtl { direction: rtl !important; }
      `}</style>
    </div>
  );
}

export default NewsSection;
